import styled from '@emotion/styled/macro';
import { Icon } from '@mdi/react';
import { BaseButton, Variables } from '../base';
import { TitleCard } from '../card';
import { InfiniteScrollDataTable } from '../data-table';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '../data-table/infinite-scroll-data-table.styles';
/** Number of activity-feed rows visible before scrolling. */
export const ACTIVITY_FEED_VISIBLE_ROWS = 5;
/** Approximate body row height (cell padding + single-line content). */
export const ACTIVITY_FEED_ROW_HEIGHT_PX = 48;
export const ACTIVITY_FEED_TABLE_HEADER_HEIGHT_PX = 33;

const activityFeedScrollMaxHeight = `${ACTIVITY_FEED_TABLE_HEADER_HEIGHT_PX + ACTIVITY_FEED_VISIBLE_ROWS * ACTIVITY_FEED_ROW_HEIGHT_PX}px`;

export const StyledTitleCard = styled(TitleCard)`
    justify-content: space-between;
    max-width: 100%;
    margin: 0 0 24px;
`;

export const HeaderToggleButton = styled(BaseButton)`
    min-width: auto;
    height: 32px;
    width: 32px;
    padding: 0;
    border-color: transparent;
    background: none;

    &:hover {
        border: solid 1px ${Variables.Colors.nx8};
        background: ${Variables.Colors.n5};
    }
`;

export const HeaderToggleIcon = styled(Icon)`
    fill: ${Variables.Colors.p300};
`;

export const CardBody = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 100%;
    gap: 16px;
    overflow-x: auto;
    padding: 24px 24px 0;
`;

export const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-height: 240px;
    padding: 48px 24px;
`;

export const ErrorContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 48px 24px;
    min-height: 240px;
`;

export const ErrorMessage = styled.p`
    margin: 0;
    max-width: 480px;
    font-size: 16px;
    line-height: 1.4;
    color: ${Variables.ThemeLight.TextMedium};
    text-align: center;
`;

export const RetryButton = styled(BaseButton)`
    min-width: 120px;
`;

export const TableCard = styled.section`
    border: 1px solid ${Variables.Colors.n15};
    border-radius: 8px;
    overflow: hidden;
    background: ${Variables.Colors.white};
`;

export const StageTag = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${Variables.ThemeLight.TextStrong};
    background-color: ${Variables.Colors.white};
    border: 1px solid ${Variables.Colors.platinum};
    border-radius: ${Variables.Rounded.medium};
    padding: 2px 6px;
`;

export const DescriptionText = styled.div`
    width: 100%;
    min-width: 0;
    white-space: normal;
    overflow-wrap: anywhere;
    line-height: 1.4;
`;

export const DescriptionLoopRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 6px;
    width: 100%;
    min-width: 0;
    color: ${Variables.ThemeLight.WarningConstant6};
    line-height: 1.4;

    svg {
        flex-shrink: 0;
        margin-top: 2px;
        color: inherit;
    }

    > span {
        min-width: 0;
        flex: 1;
        white-space: normal;
        overflow-wrap: anywhere;
    }
`;

export const ActivityFeedTable = styled(InfiniteScrollDataTable)`
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: hidden;
    background: ${Variables.Colors.white};

    ${TableContainer} {
        border: none;
        box-shadow: none;
        border-radius: 0;
        overflow: auto;
        min-height: 0;
        max-height: ${activityFeedScrollMaxHeight};
        width: 100%;
    }

    ${Table} {
        width: 100%;
        min-width: 100% !important;
    }

    /* useFlexLayout sets a fixed maxWidth on rows; allow the table to grow when the layout widens (e.g. nav closed). */
    ${TableHead} ${TableRow},
    ${TableBody} ${TableRow} {
        max-width: none !important;
        min-width: 100% !important;
    }

    /* Description column (last): flex to fill remaining width instead of staying at initial pixel width. */
    ${TableHead} ${TableRow} > ${TableHeader}:last-of-type,
    ${TableBody} ${TableRow} > ${TableCell}:last-of-type {
        flex: 1 1 auto !important;
        width: auto !important;
        min-width: 400px !important;
        max-width: none !important;
    }

    ${TableBody} {
        min-width: 100% !important;
        width: 100%;
    }

    ${TableHead} {
        background: ${Variables.ThemeLight.Background2};

        ${TableHeader} {
            background: ${Variables.Colors.white} !important;
            border-top: none !important;
            border-bottom: 1px solid ${Variables.Colors.n15} !important;
            border-right: none !important;
            color: ${Variables.ThemeLight.TextStrong};
            font-size: 16px;
            font-weight: 500;
            padding: 4px 12px;
        }
    }

    ${TableBody} {
        ${TableRow} {
            border-bottom: 1px solid ${Variables.Colors.n15};
            background: ${Variables.Colors.white};
            color: ${Variables.ThemeLight.TextMedium};
            font-size: 16px;
            font-weight: 400;

            &:hover {
                background: ${Variables.Swatches.Gray1};
            }
        }

        ${TableCell} {
            white-space: normal;
            overflow: visible;
            align-items: flex-start;
            min-width: 0;
            flex-shrink: 1;
        }
    }
`;
